import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ChatState {
  unreadCounts: Record<string, number>; // environmentId -> unread count
  activeSessions: Record<string, string>; // environmentId -> sessionId
}

interface ChatContextType {
  chatState: ChatState;
  getUnreadCount: (environmentId: string) => number;
  setUnreadCount: (environmentId: string, count: number) => void;
  incrementUnreadCount: (environmentId: string) => void;
  clearUnreadCount: (environmentId: string) => void;
  setActiveSession: (environmentId: string, sessionId: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [chatState, setChatState] = useState<ChatState>({
    unreadCounts: {},
    activeSessions: {}
  });

  const getUnreadCount = (environmentId: string): number => {
    return chatState.unreadCounts[environmentId] || 0;
  };

  const setUnreadCount = (environmentId: string, count: number) => {
    setChatState(prev => ({
      ...prev,
      unreadCounts: {
        ...prev.unreadCounts,
        [environmentId]: Math.max(0, count)
      }
    }));
  };

  const incrementUnreadCount = (environmentId: string) => {
    setChatState(prev => ({
      ...prev,
      unreadCounts: {
        ...prev.unreadCounts,
        [environmentId]: (prev.unreadCounts[environmentId] || 0) + 1
      }
    }));
  };

  const clearUnreadCount = (environmentId: string) => {
    setChatState(prev => ({
      ...prev,
      unreadCounts: {
        ...prev.unreadCounts,
        [environmentId]: 0
      }
    }));
  };

  const setActiveSession = (environmentId: string, sessionId: string) => {
    setChatState(prev => ({
      ...prev,
      activeSessions: {
        ...prev.activeSessions,
        [environmentId]: sessionId
      }
    }));
  };

  return (
    <ChatContext.Provider value={{
      chatState,
      getUnreadCount,
      setUnreadCount,
      incrementUnreadCount,
      clearUnreadCount,
      setActiveSession
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};